import React, { useEffect, useRef } from 'react';
import { CheckCircle, Heart, Users, Shield, Globe } from 'lucide-react';
import Container from '../ui/Container';

const About = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const valueRefs = useRef<(HTMLDivElement | null)[]>([]);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    valueRefs.current.forEach((ref) => {
      if (ref) {
        observer.observe(ref);
      }
    });

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
      valueRefs.current.forEach((ref) => {
        if (ref) {
          observer.unobserve(ref);
        }
      });
    };
  }, []);

  const values = [
    {
      icon: <Heart className="text-red-500" size={24} />,
      title: "Compassion First",
      description: "We believe in putting people's needs at the heart of everything we do"
    },
    {
      icon: <Users className="text-blue-500" size={24} />,
      title: "Community Building",
      description: "Creating networks that strengthen local communities"
    },
    {
      icon: <Shield className="text-green-500" size={24} />,
      title: "Trust & Safety",
      description: "Ensuring secure and reliable service delivery"
    },
    {
      icon: <Globe className="text-purple-500" size={24} />,
      title: "Global Impact",
      description: "Making a difference across borders and cultures"
    }
  ];

  return (
    <section id="about" className="py-16 md:py-24 bg-gray-50">
      <Container>
        <div ref={sectionRef} className="opacity-0 transition-opacity duration-1000">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">About HandsForNeeds</h2>
            <div className="w-20 h-1 bg-orange-500 mx-auto"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <div className="rounded-lg overflow-hidden shadow-xl transform transition-all duration-500 hover:scale-105">
                <img
                  src="https://images.pexels.com/photos/3184396/pexels-photo-3184396.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                  alt="Team collaboration at HandsForNeeds"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
            
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Mission & Vision</h3>
              <p className="text-gray-700 mb-6">
                At HandsForNeeds, we envision a world where help is always within reach. Our mission is to create innovative technological solutions that bridge gaps between service providers and those who need them, fostering a community of support and empowerment.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start transform transition-all duration-300 hover:translate-x-2">
                  <CheckCircle className="text-blue-900 mr-3 flex-shrink-0 mt-1" size={20} />
                  <p className="text-gray-700">Building user-centered platforms that solve real-world problems</p>
                </div>
                <div className="flex items-start transform transition-all duration-300 hover:translate-x-2">
                  <CheckCircle className="text-blue-900 mr-3 flex-shrink-0 mt-1" size={20} />
                  <p className="text-gray-700">Creating economic opportunities for service providers</p>
                </div>
                <div className="flex items-start transform transition-all duration-300 hover:translate-x-2">
                  <CheckCircle className="text-blue-900 mr-3 flex-shrink-0 mt-1" size={20} />
                  <p className="text-gray-700">Ensuring reliability, safety, and quality in every interaction</p>
                </div>
                <div className="flex items-start transform transition-all duration-300 hover:translate-x-2">
                  <CheckCircle className="text-blue-900 mr-3 flex-shrink-0 mt-1" size={20} />
                  <p className="text-gray-700">Leveraging technology to make services more accessible</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            {values.map((value, index) => (
              <div
                key={index}
                ref={el => valueRefs.current[index] = el}
                className="bg-white rounded-lg p-6 shadow-lg opacity-0 transform transition-all duration-500 hover:shadow-xl hover:-translate-y-2"
              >
                <div className="bg-gray-50 w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  {value.icon}
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h4>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
};

export default About;